import { isHex, type Hex } from 'viem';
import { z } from 'zod';
import { logTypeList, type NetworkTypes } from '@provex/eip712-utils';
import { networkTypeList, blockStatusList } from '@provex/eip712-utils/network-types';
import type { PropsAsStrings } from './types.js';
import { ccxtExchanges, dexExchanges, pricingExchangeSourcesList, type CcxtExchange, type DexExchange } from './price-source-config.js';

// ============================================================================
// Bigint Serialization Types
// ============================================================================

/**
 * Serialized bigint wrapper for JSON transport
 */
export type SerializedBigint = {
  __type__: 'bigint';
  value: string;
};

/**
 * Check if a value is a serialized bigint
 */
export const isSerializedBigint = (val: unknown): val is SerializedBigint => {
  return (
    typeof val === 'object' &&
    val !== null &&
    '__type__' in val &&
    val.__type__ === 'bigint' &&
    'value' in val &&
    typeof val.value === 'string'
  );
};

/**
 * Serialize a bigint to a tagged object
 */
export const serializeBigint = (val: bigint): SerializedBigint => ({
  __type__: 'bigint',
  value: val.toString(),
});

/**
 * Deserialize a tagged object back to bigint
 */
export const deserializeBigint = (val: SerializedBigint): bigint => {
  if (!isSerializedBigint(val)) {
    throw new Error('Invalid serialized bigint');
  }
  return BigInt(val.value);
};

// ============================================================================
// Zod Transform Helpers
// ============================================================================

/**
 * Zod schema for serialized bigint
 */
export const SerializedBigintSchema = z.object({
  __type__: z.literal('bigint'),
  value: z.string().refine((val) => /^-?\d+$/.test(val), {
    message: 'Must be a valid decimal number string',
  }),
});

/**
 * Transforms serialized bigint to bigint for incoming data (API requests)
 */
export const bigintString = SerializedBigintSchema.transform((val) => BigInt(val.value));

/**
 * Transforms bigint to serialized bigint for outgoing data (API responses)
 */
export const stringifyBigint = z.bigint().transform(serializeBigint);

/**
 * Optional bigint with default value
 * Properly handles optional bigint fields with defaults after transform
 */
export const optionalBigintWithDefault = (defaultValue: bigint) =>
  bigintString.optional().transform(val => val ?? defaultValue);

/**
 * Hex string validator
 */
export const hexString = z.custom<Hex>((val) => {
  return typeof val === 'string' && isHex(val);
}, {
  message: 'Invalid hex string'
});

// ============================================================================
// Base Schemas
// ============================================================================

export const SupportedNetworkSchema = z.object({
  networkType: z.string() as z.ZodType<NetworkTypes>,
  chainId: z.number(),
});

export const SupportedProjectNetworkSchema = SupportedNetworkSchema.extend({
  projectId: z.string(),
});

export const SacrificeInfoSchema = z.object({
  networkType: z.string() as z.ZodType<NetworkTypes>,
  chainId: z.number(),
  address: z.string(),
  addressIndex: z.number().nullable(),
});

// ============================================================================
// Request Schemas (Input - strings converted to bigints)
// ============================================================================

export const ValidateInputsSchema = z.object({
  timestamp: bigintString,
});

export const ProjectUpdateDataSchema = z.object({
  projectId: z.string(),
  projectName: z.string(),
  projectDescription: z.string(),
  projectWebsite: z.string(),
  projectLogo: z.string(),
  startTime: bigintString,
  inflectionTime: bigintString,
  endTime: bigintString,
  decayRateSingleStep: bigintString,
  decaySingleStepDelaySeconds: bigintString,
  volumeBonusRate: bigintString,
  usdToPointBaseRate: bigintString,
  volumeBonusCutoff: bigintString,
  flatBonusCutoff: bigintString,
  flatBonusRate: bigintString,
  referralRate: bigintString,
  disabled: z.boolean(),
});

export const SocialLinkSchema = z.object({
  platform: z.string().min(1),
  url: z.url(),
});

export const ProjectSocialLinksSchema = z.array(SocialLinkSchema);

export const ProjectTagsSchema = z.array(z.string());

export const RequestWithSignatureSchema = <T extends z.ZodTypeAny>(inputsSchema: T) =>
  z.object({
    signature: hexString,
    inputs: inputsSchema,
  });

export const ValidateRequestSchema = RequestWithSignatureSchema(
  z.object({
    timestamp: bigintString, // Keep as string for signature validation
  })
).extend({
  // The login signature is bound to a specific campaign. Every verification
  // site reconstructs the signed message with this projectId and the
  // matching campaign's disclaimer terms.
  projectId: z.string(),
});

/**
 * A signed request that is NOT bound to any campaign — used by the instance-admin
 * dashboard endpoints. Mirrors ValidateRequestSchema minus projectId.
 */
export const InstanceAuthRequestSchema = RequestWithSignatureSchema(
  z.object({
    timestamp: bigintString,
  })
)
export type InstanceAuthRequest = z.infer<typeof InstanceAuthRequestSchema>

/** Response of POST /api/v1/admin/instance/whoami. */
export const InstanceWhoAmIResponseSchema = z.object({
  isInstanceAdmin: z.boolean(),
})
export type InstanceWhoAmIResponse = z.infer<typeof InstanceWhoAmIResponseSchema>

/** A signed instance-admin request that adds or removes a label on an address. */
export const IdentityLabelMutationRequestSchema = RequestWithSignatureSchema(
  z.object({
    timestamp: bigintString,
  })
).extend({
  address: z.string(),
  label: z.string().trim().min(1).max(50),
})
export type IdentityLabelMutationRequest = z.infer<typeof IdentityLabelMutationRequestSchema>

export const IdentityLabelSchema = z.object({
  address: z.string(),
  label: z.string(),
  createdAt: z.string(),
})
export type IdentityLabel = z.infer<typeof IdentityLabelSchema>

export const IdentityLabelListResponseSchema = z.object({
  labels: z.array(IdentityLabelSchema),
})
export type IdentityLabelListResponse = z.infer<typeof IdentityLabelListResponseSchema>

export const GetAddressesRequestSchema = ValidateRequestSchema.extend({
  projectId: z.string(),
});

export const GenerateAddressRequestSchema = RequestWithSignatureSchema(
  z.object({
    projectId: z.string(),
    networkType: z.string(),
    chainId: bigintString,
    timestamp: bigintString,
  })
);

export const RegisterVoucherRequestSchema = z.object({
  projectId: z.string(),
  networkType: z.string(),
  chainId: bigintString,
  destination: z.string(),
  deadline: bigintString,
  signature: z.string(),
})
export type RegisterVoucherRequest = z.infer<typeof RegisterVoucherRequestSchema>

export const ProjectCreationRequestSchema = RequestWithSignatureSchema(
  z.object({
    timestamp: bigintString,
    projectId: z.string(),
    projectName: z.string(),
    projectDescription: z.string(),
    projectWebsite: z.string(),
    projectLogo: z.string(),
    projectTagline: z.string(),
    projectAccentColor: z.string(),
    projectAccentColorVariant: z.string(),
    projectBanner: z.string(),
    projectDisclaimerShort: z.string(),
    projectDisclaimerLong: z.string(),
    projectSocialLinks: z.string(),
    projectTags: z.string(),
    startTime: bigintString,
    inflectionTime: bigintString,
    endTime: bigintString,
    disabled: z.boolean(),
    decayRateSingleStep: bigintString,
    decaySingleStepDelaySeconds: bigintString,
    volumeBonusRate: bigintString,
    usdToPointBaseRate: bigintString,
    volumeBonusCutoff: bigintString,
    flatBonusCutoff: bigintString,
    flatBonusRate: bigintString,
    referralRate: bigintString,
    countEarlySacrifices: z.boolean(),
    useInflectionStepOne: z.boolean(),
    priceValidFromTimestamp: z.string(),
    // Genesis fields — bound by creation signature, absent from update schema (D-S3-4).
    // Empty string is the off-chain default; callers must always supply both.
    incrementMode: z.string(),
    registryAddress: z.string(),
  })
);

// ============================================================================
// Response Schemas (Output - bigints converted to strings)
// ============================================================================

export const ValidateResponseSchema = z.object({
  valid: z.boolean(),
  pointsDestinationAddress: z.string(),
  addresses: z.array(SacrificeInfoSchema),
});

export const TokenMetadataSchema = z.object({
  symbol: z.string(),
  name: z.string(),
  decimals: z.number(),
});

export const SupportedTokenSchema = SupportedNetworkSchema.extend({
  tokenAddress: z.string(),
});

export const SupportedTokenWithMetadataSchema = SupportedTokenSchema.extend({
  metadata: TokenMetadataSchema.nullable(),
  // Whether the price source is 'zero'/null (true), valid (false), or unknown (null)
  sourceIsZero: z.boolean().nullable(),
  // Optional user-friendly/search label (omit when not set)
  searchable: z.string().optional(),
});

export const ExposedPricedSacrificeDataSchema = z.object({
  projectId: z.string(),
  networkType: z.string() as z.ZodType<NetworkTypes>,
  chainId: z.number(),
  blockHash: z.string(),
  transactionHash: z.string(),
  logIndex: z.number(),
  logType: z.enum(logTypeList),
  tokenAddress: z.string(),
  pointsDestinationAddress: hexString,
  timestamp: z.string(),
  amount: z.string(),
  blockNumber: z.string(),
  blockStatus: z.string(),
  tokenSymbol: z.string(),
  tokenName: z.string(),
  tokenDecimals: z.number(),
  price: z.string().nullable(),
  priceSourceHash: z.string().nullable(),
  pointsAmount: z.string().nullable(),
  pointsType: z.string().nullable(),
  isCexAddress: z.boolean(),
});

export const UserStatsDataSchema = z.object({
  totalSacrifices: z.number(),
  totalValue: z.string(),
  totalPoints: z.string(),
  pointsVersion: z.string().nullable(),
  uniqueTokens: z.number(),
  uniqueNetworks: z.number(),
  rank: z.number().optional(), // User's rank in the leaderboard (1-indexed)
});

export const UserRoleSchema = z.object({
  role: z.enum(['admin']),
  projectId: z.string(),
  address: z.string(),
  createdAt: z.string(),
});

export const UserStatsResponseSchema = z.object({
  pointsDestinationAddress: z.string(),
  sacrifices: z.array(ExposedPricedSacrificeDataSchema),
  role: UserRoleSchema.optional().nullable(),
  referrerAddress: z.string().optional().nullable(),
  stats: UserStatsDataSchema,
});

export const NetworkInfoSchema = z.object({
  networkName: z.string(),
  networkType: z.string() as z.ZodType<NetworkTypes>,
  chainId: z.number(),
  lowerRange: z.number().optional(),
  upperRange: z.number().optional(),
  lowestBlock: z.number().optional(),
  highestBlock: z.number().optional(),
  rootAddress: z.string().optional(),
});

export const NetworkDetailsSchema = z.object({
  networkName: z.string(),
  networkType: z.string() as z.ZodType<NetworkTypes>,
  chainId: z.number(),
  rootAddress: z.string().optional(),
});

export const TokenSacrificeStatsSchema = z.object({
  stepNumber: z.string(),
  stepStartTime: z.string(),
  networkName: z.string(),
  networkType: z.string() as z.ZodType<NetworkTypes>,
  chainId: z.number(),
  tokenSymbol: z.string(),
  tokenName: z.string(),
  tokenDecimals: z.number(),
  tokenAddress: z.string(),
  count: z.string(),
  amount: z.string(),
  usdValue: z.string(),
  points: z.string().nullable(),
});

export const SacrificeMutationSchema = z.object({
  projectId: z.string(),
  networkType: z.enum(networkTypeList),
  chainId: z.number(),
  blockHash: z.string(),
  transactionHash: z.string(),
  tokenAddress: z.string(),
  logIndex: z.number(),
  logType: z.enum(logTypeList),
  action: z.enum(['ignore', 'modify', 'add']),
  amount: z.string().nullable(),
  pointsDestinationAddress: z.string().nullable(),
  userAddressListIndex: z.number().nullable(),
  source: z.enum(pricingExchangeSourcesList).nullable(),
  market: z.string().nullable(),
  timestamp: z.string().nullable(),
  blockNumber: z.string().nullable(),
  blockStatus: z.string().nullable(),
  tokenSymbol: z.string().nullable(),
  tokenName: z.string().nullable(),
  tokenDecimals: z.number().nullable(),
  price: z.string().nullable(),
  createdBy: z.string(),
  createdAt: z.string(),
  reason: z.string().nullable(),
});

export const PriceOverrideSchema = z.object({
  projectId: z.string(),
  networkType: z.enum(networkTypeList),
  chainId: z.number(),
  blockHash: z.string(),
  tokenAddress: z.string(),
  source: z.enum(pricingExchangeSourcesList),
  market: z.string(),
  price: z.string(),
  createdBy: z.string(),
  createdAt: z.string(),
  reason: z.string().nullable(),
});

export const ReportSchema = z.object({
  projectId: z.string(),
  networkType: z.enum(networkTypeList),
  chainId: z.number(),
  blockHash: z.string(),
  tokenAddress: z.string(),
  transactionHash: z.string(),
  logIndex: z.number(),
  logType: z.enum(logTypeList),
  message: z.string(),
  signature: z.string(),
  signerAddress: z.string(),
  timestamp: z.string(),
  ignored: z.boolean(),
});

export const SacrificeStatsResponseSchema = z.object({
  steps: z.array(TokenSacrificeStatsSchema),
  networks: z.array(NetworkInfoSchema),
  totalSacrifices: z.number(),
  totalPoints: z.string(),
  pointsVersion: z.string().nullable(),
  supportedTokens: z.array(SupportedTokenWithMetadataSchema),
  sacrificeMutations: z.array(SacrificeMutationSchema),
  priceOverrides: z.array(PriceOverrideSchema),
  user: UserStatsResponseSchema.optional(),
});

export const ProjectSchema = z.object({
  projectId: z.string(),
  projectName: z.string(),
  projectDescription: z.string(),
  projectDisclaimerShort: z.string().nullable(),
  projectDisclaimerLong: z.string().default(''),
  projectWebsite: z.string().nullable(),
  projectLogo: z.string().nullable(),
  projectTagline: z.string().nullable(),
  projectAccentColor: z.string().nullable(),
  projectAccentColorVariant: z.string().nullable(),
  projectBanner: z.string().nullable(),
  projectSocialLinks: ProjectSocialLinksSchema.default([]),
  projectTags: ProjectTagsSchema.default([]),
  startTime: z.string(),
  inflectionTime: z.string(),
  endTime: z.string().nullable(),
  createdAt: z.string(),
  countEarlySacrifices: z.boolean(),
});

export const FullProjectSchema = ProjectSchema.extend({
  flatBonusRate: z.string(),
  decayRateSingleStep: z.string(),
  decaySingleStepDelaySeconds: z.string(),
  volumeBonusRate: z.string(),
  usdToPointBaseRate: z.string(),
  volumeBonusCutoff: z.number(),
  flatBonusCutoff: z.number(),
  referralRate: z.string().nullable(),
  disabled: z.boolean(),
  updatedAt: z.string(),
  /**
   * When true, "at inflection" counts as decay step 1 (first decay iteration applied).
   * When false (default), "at inflection" counts as decay step 0 (no decay applied).
   */
  useInflectionStepOne: z.boolean(),
  /** CEX deposit addresses for this project — sacrifices to these earn no points */
  cexAddresses: z.array(z.string()),
  /** Genesis-immutable: 'offchain' (default) or 'onchain'. */
  incrementMode: z.string(),
  /** Non-null only when incrementMode === 'onchain'. Written once at creation. */
  registryAddress: z.string().nullable(),
  /**
   * Chain ID of the ProjectIdRegistry contract. Non-null only when
   * incrementMode === 'onchain'. Sourced from the server config so the public
   * client can independently reach the registry without trusting the backend
   * for the trust root (spec §12 / D-S6-3).
   */
  registryChainId: z.number().int().positive().nullable().default(null),
});

export const PaginationStatsSchema = z.object({
  limit: z.number().nullable(),
  offset: z.number(),
  returned: z.number(),
  totalCount: z.number(),
});
export type PaginationStats = z.infer<typeof PaginationStatsSchema>;

export const RecentSacrificesResponseSchema = z.object({
  network: NetworkDetailsSchema.optional().nullable(),
  pageInfo: PaginationStatsSchema,
  sacrifices: z.array(ExposedPricedSacrificeDataSchema),
});

/**
 * A label filter for an activity surface. `only` keeps only deposits from
 * addresses carrying any of the labels; `exclude` drops them. An empty `labels`
 * list (or an omitted filter) means no filtering.
 */
export const LabelFilterSchema = z.object({
  mode: z.enum(['only', 'exclude']),
  labels: z.array(z.string()),
})
export type LabelFilter = z.infer<typeof LabelFilterSchema>

/** Signed instance request for the cross-campaign deposit feed (paginated, newest first). */
export const InstanceActivityRequestSchema = RequestWithSignatureSchema(
  z.object({ timestamp: bigintString }),
).extend({
  limit: z.number().int().positive().max(200).optional(),
  offset: z.number().int().nonnegative().optional(),
  filter: LabelFilterSchema.optional(),
})
export type InstanceActivityRequest = z.infer<typeof InstanceActivityRequestSchema>

export const InstanceActivityResponseSchema = z.object({
  pageInfo: PaginationStatsSchema,
  sacrifices: z.array(ExposedPricedSacrificeDataSchema),
})
export type InstanceActivityResponse = z.infer<typeof InstanceActivityResponseSchema>

/** One row of the cross-campaign campaigns overview. */
export const InstanceCampaignSummarySchema = z.object({
  projectId: z.string(),
  projectName: z.string(),
  status: z.enum(['upcoming', 'live', 'ended']),
  isTest: z.boolean(),
  depositCount: z.string(),
  uniqueDepositors: z.string(),
  totalUsd: z.string(),
})
export type InstanceCampaignSummary = z.infer<typeof InstanceCampaignSummarySchema>

export const InstanceCampaignsRequestSchema = RequestWithSignatureSchema(
  z.object({ timestamp: bigintString }),
).extend({ filter: LabelFilterSchema.optional() })
export type InstanceCampaignsRequest = z.infer<typeof InstanceCampaignsRequestSchema>

export const InstanceCampaignsResponseSchema = z.object({
  campaigns: z.array(InstanceCampaignSummarySchema),
})
export type InstanceCampaignsResponse = z.infer<typeof InstanceCampaignsResponseSchema>

/** One row of the per-identity rollup, grouped by points-destination address. */
export const InstanceIdentitySummarySchema = z.object({
  address: z.string(),
  totalUsd: z.string(),
  campaignCount: z.string(),
  depositCount: z.string(),
  firstSeen: z.string().nullable(),
  lastSeen: z.string().nullable(),
  labels: z.array(z.string()),
})
export type InstanceIdentitySummary = z.infer<typeof InstanceIdentitySummarySchema>

export const InstanceIdentitiesRequestSchema = RequestWithSignatureSchema(
  z.object({ timestamp: bigintString }),
).extend({
  filter: LabelFilterSchema.optional(),
  limit: z.number().int().positive().max(500).optional(),
  offset: z.number().int().nonnegative().optional(),
})
export type InstanceIdentitiesRequest = z.infer<typeof InstanceIdentitiesRequestSchema>

export const InstanceIdentitiesResponseSchema = z.object({
  identities: z.array(InstanceIdentitySummarySchema),
})
export type InstanceIdentitiesResponse = z.infer<typeof InstanceIdentitiesResponseSchema>

/** One campaign-lifecycle tally in a cycle snapshot. */
export const CycleLifecycleCountsSchema = z.object({
  pending: z.number().int().nonnegative(),
  open: z.number().int().nonnegative(),
  closing: z.number().int().nonnegative(),
  swept: z.number().int().nonnegative(),
  failed: z.number().int().nonnegative(),
})
export type CycleLifecycleCounts = z.infer<typeof CycleLifecycleCountsSchema>

/** One chain's spend in a cycle snapshot. Native is a base-unit string (bigint). */
export const CycleChainSpendSchema = z.object({
  networkType: z.string(),
  chainId: z.number().int(),
  spentNative: z.string(),
  spentUsdEstimate: z.number(),
})
export type CycleChainSpend = z.infer<typeof CycleChainSpendSchema>

/** A single cycle command's most-recent run snapshot. */
export const CycleStatusSnapshotSchema = z.object({
  command: z.enum(['tick', 'deposit-drip', 'closeout']),
  enabled: z.boolean(),
  paused: z.boolean(),
  dryRun: z.boolean(),
  lifecycleCounts: CycleLifecycleCountsSchema,
  chainSpend: z.array(CycleChainSpendSchema),
  totalUsd: z.number(),
  spendCapUsd: z.number(),
  lastRunAt: z.string(),       // ISO-8601
  lastOutcome: z.string(),     // short, human-readable, secret-free
})
export type CycleStatusSnapshot = z.infer<typeof CycleStatusSnapshotSchema>

/** Response of POST /admin/instance/cycle-status. */
export const InstanceCycleStatusRequestSchema = InstanceAuthRequestSchema
export type InstanceCycleStatusRequest = z.infer<typeof InstanceCycleStatusRequestSchema>

export const InstanceCycleStatusResponseSchema = z.object({
  statuses: z.array(CycleStatusSnapshotSchema),
})
export type InstanceCycleStatusResponse = z.infer<typeof InstanceCycleStatusResponseSchema>

export const LeaderboardEntrySchema = z.object({
  pointsDestinationAddress: z.string(),
  totalAmount: z.string(),
  count: z.string(),
  referralCount: z.string(),
  pointsByType: z.record(z.string(), z.string()),
  usdValue: z.string(),
  tokens: z.array(SupportedTokenSchema),
  tokenTimestamps: z.record(z.string(), z.string()),
  projectId: z.string(),
  version: z.string(),
});

export const LeaderboardResponseSchema = z.object({
  leaderboard: z.array(LeaderboardEntrySchema),
  pageInfo: PaginationStatsSchema,
  pointsVersion: z.string().nullable(),
});

export const DirectoryStatsSchema = z.object({
  /** Whole-dollar USD raised for the campaign's latest points version, as a string. */
  raisedUsd: z.string(),
  /** Distinct sacrificer (points-destination) addresses for that version. */
  sacrificerCount: z.number().int().nonnegative(),
})

/** Per-campaign rollups keyed by projectId, returned by GET /directory/stats. */
export const DirectoryStatsResponseSchema = z.record(z.string(), DirectoryStatsSchema)

export type DirectoryStats = z.infer<typeof DirectoryStatsSchema>
export type DirectoryStatsResponse = z.infer<typeof DirectoryStatsResponseSchema>

/** Available options for ordering the leaderboard */
export const leaderboardOrderByOptions = ['points', 'usd', 'volume'] as const;

/** Union type for leaderboard ordering options */
export type LeaderboardOrderBy = typeof leaderboardOrderByOptions[number];

/** Whitelisted columns for ordering the sacrifices feed. `usd` = price-normalized value (raw per-token amount is intentionally not sortable across tokens of differing decimals). */
export const sacrificeOrderByOptions = ['timestamp', 'usd'] as const;

/** Union type for sacrifices ordering options */
export type SacrificeOrderBy = typeof sacrificeOrderByOptions[number];

export const LeaderboardRequestSchema = z.object({
  projectId: z.string(),
  version: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(1000).optional().default(100),
  offset: z.coerce.number().int().min(0).optional().default(0),
  orderDirection: z.enum(['asc', 'desc']).optional().default('desc'),
  orderBy: z.enum(leaderboardOrderByOptions).optional().default('volume'),
});

export const NetworkDetailsRequestSchema = z.object({
  projectId: z.string(),
  networkType: z.enum(networkTypeList),
  chainId: z.coerce.number().int(),
});

export const UnpricedSacrificesRequestSchema = z.object({
  projectId: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(1000).optional().default(100),
  offset: z.coerce.number().int().min(0).optional().default(0),
});

export const SacrificeQueryRequestSchema = z.object({
  projectId: z.string(),
  networkType: z.enum(networkTypeList).optional(),
  chainId: z.coerce.number().int().optional(),
  pointsDestinationAddress: z.string().optional(),
  tokenAddress: z.string().optional(),
  version: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(1000).optional().default(100),
  offset: z.coerce.number().int().min(0).optional().default(0),
  orderDirection: z.enum(['asc', 'desc']).optional().default('desc'),
  orderBy: z.enum(sacrificeOrderByOptions).optional().default('timestamp'),
});

export const ErrorResponseSchema = z.object({
  error: z.string(),
});

export const BlockDataSchema = z.object({
  number: bigintString,
  hash: z.string(),
  timestamp: z.string(), // ISO string
});

export const NetworkBlockInfoSchema = z.object({
  networkType: z.string(),
  chainId: z.number(),
  blocks: z.object({
    indexed: z.object({
      timestamp: z.number().nullable(),
      number: z.number().nullable(),
    }),
    finalized: z.object({
      timestamp: z.number().nullable(),
      number: z.number().nullable(),
    }),
    latest: z.object({
      timestamp: z.number().nullable(),
      number: z.number().nullable(),
    }),
  }),
});

export const HealthResponseSchema = z.object({
  status: z.string(),
  timestamp: z.union([z.number(), z.string()]),
});

export const ProgressResponseSchema = z.array(NetworkBlockInfoSchema);

export const ReferralSubmissionInputsSchema = z.object({
  projectId: z.string(),
  referrerAddress: z.string(),
  timestamp: bigintString,
});

export const ReferralSubmissionRequestSchema = RequestWithSignatureSchema(
  ReferralSubmissionInputsSchema
);

export const ReferralDataSchema = z.object({
  projectId: z.string(),
  pointsDestinationAddress: z.string(),
  referrerAddress: z.string(),
});

export const ReferralSchema = z.object({
  projectId: z.string(),
  pointsDestinationAddress: z.string(),
  referrerAddress: z.string(),
  startAt: z.string(),
  endedAt: z.string().nullable(),
});

export const ReferralSubmissionResponseSchema = z.object({
  success: z.boolean(),
  message: z.string(),
  referral: ReferralDataSchema,
});

export const ReferralListResponseSchema = z.object({
  projectId: z.string(),
  pointsDestinationAddress: z.string(),
  referrals: z.array(ReferralSchema),
});

export const ReportSubmissionInputsSchema = z.object({
  networkType: z.string() as z.ZodType<NetworkTypes>,
  chainId: bigintString,
  blockHash: z.string(),
  tokenAddress: z.string(),
  logIndex: z.number(),
  logType: z.enum(logTypeList),
  transactionHash: z.string(),
  message: z.string(),
  timestamp: bigintString,
  projectId: z.string(),
});

export const ReportSubmissionRequestSchema = RequestWithSignatureSchema(
  ReportSubmissionInputsSchema
);

export const ReportSubmissionResponseSchema = z.object({
  success: z.boolean(),
  projectId: z.string(),
  networkType: z.string(),
  chainId: z.number(),
  blockHash: z.string(),
  tokenAddress: z.string(),
  logIndex: z.number(),
  logType: z.enum(logTypeList),
  transactionHash: z.string(),
  message: z.string(),
});

export const LatestBlockRequestSchema = ValidateRequestSchema.extend({
  networkType: z.string() as z.ZodType<NetworkTypes>,
  chainId: z.number(),
  projectId: z.string(),
});

export const RetrieveStatsRequestSchema = ValidateRequestSchema.partial().extend({
  projectId: z.string(),
  address: z.string().optional().nullable(),
  version: z.string().optional().nullable(),
});

// Market price request schemas
export const MarketPricePairSchema = z.object({
  exchange: z.string().refine(
    (val) => ccxtExchanges.includes(val as CcxtExchange) || dexExchanges.includes(val as DexExchange),
    { message: 'Invalid exchange. Must be a supported CCXT or DEX exchange.' }
  ),
  market: z.string().min(1, 'Market symbol or pair address is required')
}).refine(
  (data) => {
    // For CCXT exchanges, enforce BASE/QUOTE format
    if (ccxtExchanges.includes(data.exchange as CcxtExchange)) {
      return /^[A-Z0-9]+\/[A-Z0-9]+$/.test(data.market);
    }
    // For DEX exchanges, allow pair addresses or BASE/QUOTE format
    return true;
  },
  { message: 'CCXT markets must be in format BASE/QUOTE (e.g., BTC/USDT)' }
);

export const MarketPriceRequestSchema = z.array(MarketPricePairSchema)
  .min(1, 'At least one exchange+market pair is required')
  .max(200, 'Maximum 200 pairs allowed per request');

export const ProjectCreationResponseSchema = FullProjectSchema.pick({
  projectId: true,
  projectName: true,
  projectDescription: true,
  projectWebsite: true,
  projectLogo: true,
  startTime: true,
  inflectionTime: true,
  endTime: true,
  decayRateSingleStep: true,
  decaySingleStepDelaySeconds: true,
  volumeBonusRate: true,
  usdToPointBaseRate: true,
  volumeBonusCutoff: true,
  flatBonusCutoff: true,
  flatBonusRate: true,
  createdAt: true,
  updatedAt: true,
});

export const ProjectsResponseSchema = z.array(FullProjectSchema);

export const ProjectNetworksResponseSchema = z.array(NetworkDetailsSchema);

// Admin schemas
export const AdminProjectsListItemSchema = z.object({
  projectId: z.string(),
  projectName: z.string(),
  projectDescription: z.string(),
  projectWebsite: z.string().nullable(),
  projectLogo: z.string().nullable(),
  role: z.string(),
  createdAt: z.string(),
});

export const AdminProjectsResponseSchema = z.array(AdminProjectsListItemSchema);

export const AdminProjectRequestSchema = ValidateRequestSchema.extend({
  projectId: z.string(),
});

export const PriceSourceSchema = z.object({
  projectId: z.string(),
  networkType: z.string() as z.ZodType<NetworkTypes>,
  chainId: z.number(),
  tokenAddress: z.string(),
  source: z.string(),
  market: z.string(),
  searchable: z.string().nullable(),
});

export const FullProjectNetworkSchema = SupportedProjectNetworkSchema.extend({
  networkName: z.string(),
  lowerRange: z.number().nullable(),
  upperRange: z.number().nullable(),
  lastFinalizedBlock: z.number().nullable(),
  lowestBlock: z.number(),
  highestBlock: z.number().nullable(),
  rootAddress: z.string(),
  rpcUrls: z.string().nullable(),
  nextIndex: z.number(),
  /** Genesis-immutable publication mode. Null means not yet published (draft or legacy row). */
  publicationMode: z.string().nullable().optional(),
});

export const GlobalNetworkSchema = SupportedNetworkSchema.extend({
  networkName: z.string(),
  collectorKey: z.string(),
});

export const CommittedRangeSchema = z.object({
  rangeStart: z.number(),
  count: z.number(),
  merkleRoot: z.string(),
});

export const AnchoredRangeSchema = z.object({
  rangeStart: z.number(),
  count: z.number(),
  root: z.string(),
});

export const AddressReadinessSchema = z.object({
  networkType: z.string() as z.ZodType<NetworkTypes>,
  chainId: z.number(),
  generated: z.number(),
  committedRanges: z.array(CommittedRangeSchema).default([]),
  anchoredRanges: z.array(AnchoredRangeSchema).default([]),
});

export const AdminProjectResponseSchema = z.object({
  project: FullProjectSchema,
  roles: z.array(UserRoleSchema),
  networks: z.array(FullProjectNetworkSchema),
  priceSources: z.array(PriceSourceSchema),
  globalNetworks: z.array(GlobalNetworkSchema),
  exchangesByRank: z.object({
    ccxt: z.array(z.array(z.enum(ccxtExchanges))),
    dex: z.array(z.array(z.enum(dexExchanges))),
    stable: z.array(z.array(z.enum(['']))),
  }),
  addressReadiness: z.array(AddressReadinessSchema),
  /** Chain ID hosting the ProjectIdRegistry contract. Null for off-chain projects. */
  registryChainId: z.number().nullable().optional(),
});

export const NetworkManagementInputsSchema = z.object({
  projectId: z.string(),
  networkType: z.string(),
  chainId: bigintString,
  action: z.enum(['add', 'remove', 'update']),
  timestamp: bigintString,
  rootAddress: z.string().optional().default(''),
  networkName: z.string().optional().default(''),
  lowestBlock: optionalBigintWithDefault(0n),
  lowerRange: optionalBigintWithDefault(0n),
  upperRange: optionalBigintWithDefault(0n),
  rpcUrls: z.string().optional().default(''),
});

export const NetworkManagementRequestSchema = RequestWithSignatureSchema(
  NetworkManagementInputsSchema
);

export const NetworkManagementResponseSchema = z.object({
  success: z.boolean(),
  action: z.string(),
  message: z.string(),
  network: z.union([NetworkDetailsSchema, z.object({
    projectId: z.string(),
    networkType: z.string(),
    chainId: z.number(),
  })]).nullable(),
});

export const AdminManagementInputsSchema = z.object({
  projectId: z.string(),
  address: z.string(),
  action: z.enum(['add', 'remove']),
  timestamp: bigintString,
});

export const AdminManagementRequestSchema = RequestWithSignatureSchema(
  AdminManagementInputsSchema
);

export const AdminManagementResponseSchema = z.object({
  success: z.boolean(),
  action: z.string(),
  message: z.string(),
  admin: z
    .object({
      projectId: z.string(),
      address: z.string(),
    })
    .nullable(),
});

export const ProjectUpdateInputsSchema = z.object({
  projectId: z.string(),
  timestamp: bigintString,
  projectName: z.string(),
  projectWebsite: z.string(),
  projectDescription: z.string(),
  projectLogo: z.string(),
  projectTagline: z.string(),
  projectAccentColor: z.string(),
  projectAccentColorVariant: z.string(),
  projectBanner: z.string(),
  projectDisclaimerShort: z.string(),
  projectDisclaimerLong: z.string(),
  projectSocialLinks: z.string(),
  projectTags: z.string(),
  startTime: bigintString,
  inflectionTime: bigintString,
  endTime: bigintString,
  decaySingleStepDelaySeconds: bigintString,
  volumeBonusCutoff: bigintString,
  flatBonusCutoff: bigintString,
  decayRateSingleStep: bigintString,
  usdToPointBaseRate: bigintString,
  flatBonusRate: bigintString,
  referralRate: bigintString,
  countEarlySacrifices: z.boolean(),
  useInflectionStepOne: z.boolean(),
});

export const ProjectUpdateRequestSchema = RequestWithSignatureSchema(
  ProjectUpdateInputsSchema
);

export const ProjectUpdateResponseSchema = z.object({
  success: z.boolean(),
  message: z.string(),
});

export const LatestBlockResponseSchema = z.object({
  blockNumber: z.number(),
  timestamp: z.union([z.number(), bigintString]),
  hash: z.string().nullable(),
});

export const TokenManagementInputsSchema = z.object({
  projectId: z.string(),
  networkType: z.string(),
  chainId: bigintString,
  action: z.enum(['add', 'remove', 'update']),
  timestamp: bigintString,
  tokenAddress: z.string(),
  source: z.string().optional().default(''),
  market: z.string().optional().default(''),
  searchable: z.string().optional().default(''),
});

export const TokenManagementRequestSchema = RequestWithSignatureSchema(
  TokenManagementInputsSchema
);

export const TokenManagementResponseSchema = z.object({
  success: z.boolean(),
  action: z.string(),
  message: z.string(),
  token: z.union([
    PriceSourceSchema,
    z.object({
      projectId: z.string(),
      networkType: z.string(),
      chainId: z.number(),
      tokenAddress: z.string(),
    })
  ]),
});

// Full-payload request schemas for the two dedicated admin action signatures.
// Every field the handler writes is validated here AND bound by the signature
// (see sacrificeMutationSigner / reportToggleSigner in @provex/eip712-utils).
export const SacrificeMutationUpsertInputsSchema = z.object({
  projectId: z.string().min(1),
  networkType: z.enum(networkTypeList),
  // chainId 0 is a valid network (Bitcoin, networkType bip32): the constraint
  // is non-negative integer, never falsiness.
  chainId: z.number().int().nonnegative(),
  blockHash: z.string().min(1),
  transactionHash: z.string().min(1),
  tokenAddress: z.string().min(1),
  logIndex: z.number().int().nonnegative(),
  logType: z.enum(logTypeList),
  action: z.enum(['ignore', 'modify', 'add']),
  timestamp: bigintString,
  amount: z.string().optional(),
  pointsDestinationAddress: z.string().optional(),
  userAddressListIndex: z.number().int().nonnegative().optional(),
  source: z.enum(pricingExchangeSourcesList).optional(),
  market: z.string().optional(),
  // The sacrifice row's own timestamp ('add' action); distinct from the
  // signature timestamp above.
  sacrificeTimestamp: z.string().optional(),
  blockNumber: z.string().optional(),
  blockStatus: z.enum(blockStatusList).optional(),
  tokenSymbol: z.string().optional(),
  tokenName: z.string().optional(),
  tokenDecimals: z.number().int().nonnegative().optional(),
  price: z.string().optional(),
  reason: z.string().optional(),
});

export const SacrificeMutationUpsertRequestSchema = RequestWithSignatureSchema(
  SacrificeMutationUpsertInputsSchema
);

export const ReportToggleInputsSchema = z.object({
  projectId: z.string().min(1),
  networkType: z.enum(networkTypeList),
  chainId: z.number().int().nonnegative(),
  // blockHash / tokenAddress / transactionHash may be empty strings: general
  // reports are not tied to a specific sacrifice.
  blockHash: z.string(),
  tokenAddress: z.string(),
  transactionHash: z.string(),
  logIndex: z.number().int().nonnegative(),
  logType: z.enum(logTypeList),
  signerAddress: z.string().min(1),
  ignored: z.boolean(),
  timestamp: bigintString,
});

export const ReportToggleRequestSchema = RequestWithSignatureSchema(
  ReportToggleInputsSchema
);

export const AdminReferralsResponseSchema = z.object({
  projectId: z.string(),
  referrals: z.array(ReferralSchema),
});

export const AdminReportsResponseSchema = z.object({
  projectId: z.string(),
  reports: z.array(ReportSchema),
});

export const AdminSacrificeMutationsResponseSchema = z.object({
  projectId: z.string(),
  mutations: z.array(SacrificeMutationSchema),
});

export const AdminPriceOverridesResponseSchema = z.object({
  projectId: z.string(),
  overrides: z.array(PriceOverrideSchema),
});

// Admin details schemas
export const BlockDetailsSchema = z.object({
  networkType: z.enum(networkTypeList),
  chainId: z.number(),
  blockNumber: z.string(),
  blockHash: z.string(),
  timestamp: z.string(),
  status: z.string(),
});

export const TransactionDetailsSchema = z.object({
  networkType: z.enum(networkTypeList),
  chainId: z.number(),
  blockHash: z.string(),
  transactionHash: z.string(),
  transactionIndex: z.number(),
  from: z.string(),
  to: z.string(),
});

export const TokenDetailsSchema = z.object({
  networkType: z.enum(networkTypeList),
  chainId: z.number(),
  tokenAddress: z.string(),
  symbol: z.string(),
  name: z.string(),
  decimals: z.number(),
});

export const LogDetailsSchema = z.object({
  networkType: z.enum(networkTypeList),
  chainId: z.number(),
  blockHash: z.string(),
  transactionHash: z.string(),
  logIndex: z.number(),
  logType: z.enum(logTypeList),
});

export const ReportToggleResponseSchema = z.object({
  success: z.boolean(),
  report: ReportSchema,
});

export const SacrificeDetailsResponseSchema = z.object({
  projectId: z.string(),
  block: BlockDetailsSchema,
  transaction: TransactionDetailsSchema,
  token: TokenDetailsSchema,
  log: LogDetailsSchema,
});

export const SacrificeMutationUpsertResponseSchema = z.object({
  success: z.boolean(),
  mutation: SacrificeMutationSchema,
});

// ============================================================================
// Download Schemas
// ============================================================================

/**
 * Response body for POST /address when the registry has not yet issued an index
 * for the destination address. The client should display a "pending registration"
 * state and poll or wait for the on-chain relay to complete.
 *
 * `relayJob` is populated by Stage 4 once the relay worker is wired up; the field
 * is optional from day one so the client contract does not change shape later.
 */
export const PendingRegistrationSchema = z.object({
  status: z.literal('pending_registration'),
  registryAddress: z.string(),
  networkKey: z.string(),
  relayJob: z.object({ status: z.string(), txHash: z.string().nullable() }).optional(),
})
export type PendingRegistration = z.infer<typeof PendingRegistrationSchema>

export const DistributionRowSchema = z.object({
  address: z.string(),
  points: z.string(),
});

export const PointBreakdownRowSchema = z.object({
  address: z.string(),
  points: z.string(),
  pointType: z.string(),
  networkType: z.string().nullable(),
  networkName: z.string().nullable(),
  chainId: z.number().nullable(),
  tokenSymbol: z.string().nullable(),
  tokenAddress: z.string().nullable(),
  tokenDecimals: z.number().nullable(),
  sacrificeAmount: z.string().nullable(),
  price: z.string().nullable(),
  blockTimestamp: z.string().nullable(),
  step: z.number().nullable(),
  rate: z.string().nullable(),
  blockNumber: z.string().nullable(),
  blockHash: z.string().nullable(),
  transactionHash: z.string().nullable(),
  logIndex: z.number().nullable(),
  logType: z.string().nullable(),
});

export const AuditRowSchema = z.object({
  networkType: z.string(),
  networkName: z.string(),
  chainId: z.number(),
  blockNumber: z.string(),
  blockHash: z.string(),
  blockTimestamp: z.string(),
  transactionHash: z.string(),
  logIndex: z.number(),
  logType: z.string(),
  tokenSymbol: z.string(),
  tokenAddress: z.string(),
  tokenDecimals: z.number(),
  amount: z.string(),
  pointsDestinationAddress: z.string(),
  price: z.string().nullable(),
  priceSourceHash: z.string().nullable(),
  step: z.number().nullable(),
  rate: z.string().nullable(),
});

export const DistributionDownloadResponseSchema = z.object({
  addresses: z.array(DistributionRowSchema),
  version: z.string().nullable(),
  contentsHash: z.string().nullable(),
});

export const PointBreakdownDownloadResponseSchema = z.object({
  pointBreakdown: z.array(PointBreakdownRowSchema),
  version: z.string(),
  statistics: z.object({
    totalAddresses: z.number(),
    totalPoints: z.string(),
    totalEntries: z.number(),
  }),
});

export const AuditDownloadResponseSchema = z.object({
  projectInfo: z.any(), // Complex nested object - can be typed more strictly if needed
  pointsVersion: z.string().nullable(),
  generatedAt: z.string(),
  networks: z.array(z.any()),
  blocks: z.array(z.any()),
  transactions: z.array(z.any()),
  sacrifices: z.array(AuditRowSchema),
  priceSources: z.array(z.any()),
  supportedTokens: z.array(z.any()),
  sacrificeMutations: z.array(z.any()),
  priceOverrides: z.array(z.any()),
  statistics: z.object({
    totalNetworks: z.number(),
    totalBlocks: z.number(),
    totalTransactions: z.number(),
    totalSacrifices: z.number(),
    totalPriceSources: z.number(),
    totalMutations: z.number(),
    totalPriceOverrides: z.number(),
  }),
});

// ============================================================================
// TypeScript Types (Inferred from Schemas)
// ============================================================================

// All types should be inferred from schemas
// Input types
export type ValidateInputs = z.infer<typeof ValidateInputsSchema>;
export type ReferralSubmissionInputs = z.infer<typeof ReferralSubmissionInputsSchema>;
export type ReportSubmissionInputs = z.infer<typeof ReportSubmissionInputsSchema>;
export type NetworkManagementInputs = z.infer<typeof NetworkManagementInputsSchema>;
export type ProjectUpdateInputs = z.infer<typeof ProjectUpdateInputsSchema>;
export type TokenManagementInputs = z.infer<typeof TokenManagementInputsSchema>;
export type SacrificeMutationUpsertInputs = z.infer<typeof SacrificeMutationUpsertInputsSchema>;
export type SacrificeMutationUpsertRequest = z.infer<typeof SacrificeMutationUpsertRequestSchema>;
export type ReportToggleInputs = z.infer<typeof ReportToggleInputsSchema>;
export type ReportToggleRequest = z.infer<typeof ReportToggleRequestSchema>;

// Request types
export type ProjectUpdateData = z.infer<typeof ProjectUpdateDataSchema>;
export type ValidateRequest = z.infer<typeof ValidateRequestSchema>;
export type GetAddressesRequest = z.infer<typeof GetAddressesRequestSchema>;
export type GenerateAddressRequest = z.infer<typeof GenerateAddressRequestSchema>;
export type ProjectCreationRequest = z.infer<typeof ProjectCreationRequestSchema>;
export type ValidateResponse = z.infer<typeof ValidateResponseSchema>;
export type LatestBlockRequest = z.infer<typeof LatestBlockRequestSchema>;
export type RetrieveStatsRequest = z.infer<typeof RetrieveStatsRequestSchema>;

// Response types
export type NetworkBlockInfo = z.infer<typeof NetworkBlockInfoSchema>;
export type HealthResponse = z.infer<typeof HealthResponseSchema>;
export type HealthCheckResponse = z.infer<typeof HealthResponseSchema>;
export type ProgressResponse = z.infer<typeof ProgressResponseSchema>;
export type ReferralSubmissionRequest = z.infer<typeof ReferralSubmissionRequestSchema>;
export type ReferralSubmissionResponse = z.infer<typeof ReferralSubmissionResponseSchema>;
export type ReferralListResponse = z.infer<typeof ReferralListResponseSchema>;
export type Referral = z.infer<typeof ReferralSchema>;
export type ReportSubmissionRequest = z.infer<typeof ReportSubmissionRequestSchema>;
export type ReportSubmissionResponse = z.infer<typeof ReportSubmissionResponseSchema>;
export type ProjectCreationResponse = z.infer<typeof ProjectCreationResponseSchema>;
export type ProjectsResponse = z.infer<typeof ProjectsResponseSchema>;
export type ProjectNetworksResponse = z.infer<typeof ProjectNetworksResponseSchema>;
export type AdminProjectsResponse = z.infer<typeof AdminProjectsResponseSchema>;
export type AdminProjectRequest = z.infer<typeof AdminProjectRequestSchema>;
export type AdminProjectResponse = z.infer<typeof AdminProjectResponseSchema>;
export type AddressReadiness = z.infer<typeof AddressReadinessSchema>;
export type AnchoredRange = z.infer<typeof AnchoredRangeSchema>;
export type FullProject = z.infer<typeof FullProjectSchema>;
export type NetworkManagementRequest = z.infer<typeof NetworkManagementRequestSchema>;
export type NetworkManagementResponse = z.infer<typeof NetworkManagementResponseSchema>;
export type AdminManagementInputs = z.infer<typeof AdminManagementInputsSchema>;
export type AdminManagementRequest = z.infer<typeof AdminManagementRequestSchema>;
export type AdminManagementResponse = z.infer<typeof AdminManagementResponseSchema>;
export type ProjectUpdateRequest = z.infer<typeof ProjectUpdateRequestSchema>;
export type ProjectUpdateResponse = z.infer<typeof ProjectUpdateResponseSchema>;
export type LatestBlockResponse = z.infer<typeof LatestBlockResponseSchema>;
export type TokenManagementRequest = z.infer<typeof TokenManagementRequestSchema>;
export type TokenManagementResponse = z.infer<typeof TokenManagementResponseSchema>;
export type PriceSource = z.infer<typeof PriceSourceSchema>;
export type FullProjectNetwork = z.infer<typeof FullProjectNetworkSchema>;
export type GlobalNetwork = z.infer<typeof GlobalNetworkSchema>;
export type MarketPricePair = z.infer<typeof MarketPricePairSchema>;
export type MarketPriceRequest = z.infer<typeof MarketPriceRequestSchema>;
export type Report = z.infer<typeof ReportSchema>;
export type AdminReferralsResponse = z.infer<typeof AdminReferralsResponseSchema>;
export type AdminReportsResponse = z.infer<typeof AdminReportsResponseSchema>;
export type AdminSacrificeMutationsResponse = z.infer<typeof AdminSacrificeMutationsResponseSchema>;
export type AdminPriceOverridesResponse = z.infer<typeof AdminPriceOverridesResponseSchema>;
export type BlockDetails = z.infer<typeof BlockDetailsSchema>;
export type TransactionDetails = z.infer<typeof TransactionDetailsSchema>;
export type TokenDetails = z.infer<typeof TokenDetailsSchema>;
export type LogDetails = z.infer<typeof LogDetailsSchema>;
export type ReportToggleResponse = z.infer<typeof ReportToggleResponseSchema>;
export type SacrificeDetailsResponse = z.infer<typeof SacrificeDetailsResponseSchema>;
export type SacrificeMutationUpsertResponse = z.infer<typeof SacrificeMutationUpsertResponseSchema>;
export type SacrificeInfo = z.infer<typeof SacrificeInfoSchema>;
export type SacrificeStatsResponse = z.infer<typeof SacrificeStatsResponseSchema>;
export type RecentSacrificesResponse = z.infer<typeof RecentSacrificesResponseSchema>;
export type ExposedPricedSacrificeData = z.infer<typeof ExposedPricedSacrificeDataSchema>;
export type UserRole = z.infer<typeof UserRoleSchema>;
export type UserStatsData = z.infer<typeof UserStatsDataSchema>;
export type UserStatsResponse = z.infer<typeof UserStatsResponseSchema>;
export type BlockData = z.infer<typeof BlockDataSchema>;

// Download types
export type DistributionRow = z.infer<typeof DistributionRowSchema>;
export type PointBreakdownRow = z.infer<typeof PointBreakdownRowSchema>;
export type AuditRow = z.infer<typeof AuditRowSchema>;
export type DistributionDownloadResponse = z.infer<typeof DistributionDownloadResponseSchema>;
export type PointBreakdownDownloadResponse = z.infer<typeof PointBreakdownDownloadResponseSchema>;
export type AuditDownloadResponse = z.infer<typeof AuditDownloadResponseSchema>;

// ============================================================================
// Validation & Transformation Utilities
// ============================================================================

/**
 * Parse and validate request data with automatic type conversion
 */
export const parseRequest = <T extends z.ZodTypeAny>(
  schema: T,
  data: unknown
): z.infer<T> => {
  return schema.parse(data);
};

/**
 * Safe parse with error handling
 */
export const safeParseRequest = <T extends z.ZodTypeAny>(
  schema: T,
  data: unknown
): { success: true; data: z.infer<T> } | { success: false; error: z.ZodError } => {
  const result = schema.safeParse(data);
  if (result.success) {
    return { success: true, data: result.data };
  }
  return { success: false, error: result.error };
};

/**
 * Serialize a request for JSON transport
 * Uses custom JSON serializer that handles bigints
 */
export const serializeRequest = <T extends z.ZodTypeAny>(
  schema: T,
  data: z.infer<T>
): string => {
  // Validate the data with the schema
  const validated = schema.parse(data);

  // Use JSON.stringify with a custom replacer to handle bigints
  return JSON.stringify(validated, (_key, value) => {
    if (typeof value === 'bigint') {
      return serializeBigint(value);
    }
    return value;
  });
};

// ============================================================================
// Enhanced Serialization Utilities
// ============================================================================

/**
 * Serialize inputs with proper bigint handling using tagged format
 */
export const serializeInputs = <T extends object>(inputs: T): Record<string, unknown> => {
  return Object.fromEntries(
    Object.entries(inputs).map(([key, value]) => {
      if (typeof value === 'bigint') {
        return [key, serializeBigint(value)];
      }
      return [key, value];
    })
  );
};

/**
 * Deserialize inputs with proper bigint handling using tagged format
 */
export const deserializeInputs = <T extends object>(inputs: Record<string, unknown>): T => {
  return Object.fromEntries(
    Object.entries(inputs).map(([key, value]) => {
      // Check if it's a serialized bigint
      if (isSerializedBigint(value)) {
        return [key, deserializeBigint(value)];
      }
      // Keep hex strings as-is
      if (typeof value === 'string' && isHex(value)) {
        return [key, value];
      }
      return [key, value];
    })
  ) as T;
};

// ============================================================================
// Legacy Serialization (Deprecated - use serializeInputs/deserializeInputs)
// ============================================================================

/**
 * @deprecated Use serializeInputs with tagged bigint format instead
 */
export const legacySerializeInputs = <T extends object>(inputs: T): PropsAsStrings<T> => {
  return Object.fromEntries(
    Object.entries(inputs).map(([key, value]) => [key, typeof value === 'bigint' ? value.toString() : value]) as [string, string | boolean | number][]
  ) as PropsAsStrings<T>
};

/**
 * @deprecated Use deserializeInputs with tagged bigint format instead
 */
export const legacyDeserializeInputs = <T extends object>(inputs: PropsAsStrings<T>): T => {
  return Object.fromEntries(
    Object.entries(inputs).map(([key, value]) => {
      const type = typeof value
      try {
        if (typeof value === 'string' && isHex(value)) {
          return [key, value]
        }
        if (type === 'string') {
          return [key, BigInt(value as string)]
        }
        return [key, value]
      } catch {
        return [key, value]
      }
    }) as [string, bigint | boolean | number | string][]
  ) as T
};
